import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isNil, last, reduce, times } from 'lodash-es';

import Skeleton from '../../../../components/Skeleton';
import { SortableTable } from '../../../../components/SortableTable';
import { numberToHumanChart } from '../../../../utils/formatter';

const getFullYear = date => date && new Date(date).getFullYear();

const tableClassNameByVariant = {
  year: 'table-layout-fixed',
  cohortYear: 'table-layout-fixed',
  quarter: '',
};

const ChartTable = ({
  show,
  variant,
  loading,
  tableData,
  tableLayoutRef,
  tableWrapperRef,
  scrollState = {},
}) => {
  const columns = useMemo(() => {
    if (loading) {
      return times(12, n => ({
        dataField: `${n}`,
        element: <Skeleton height={12} width="80%" />,
      }));
    }

    if (variant === 'year') {
      return tableData.map(([timestamp]) => ({
        className: 'cell',
        dataField: `${timestamp}`,
        text: '',
      }));
    }

    if (variant === 'cohortYear') {
      const leadRowKey = 'all_users';

      return [{
        className: 'cell',
        dataField: 'name',
        text: '',
      },
      ...(tableData.rows[leadRowKey]?.map(([timestamp]) => ({
        dataField: `${timestamp}`,
        text: tableData.headerLabels[timestamp],
      })) || [])];
    }

    if (variant === 'quarter') {
      const leadRowKey = 'prev_period';

      return [{
        className: 'cell',
        dataField: 'name',
        text: '',
      },
      ...(tableData[leadRowKey]?.map(({ date }, index) => ({
        dataField: date,
        text: `Day ${index + 1}`,
        className: cx(tableData.curr_period.length === index && 'js-scroll-selector'),
      })) || [])];
    }

    return [];
  }, [loading, tableData, variant]);

  const rows = useMemo(() => {
    if (loading) {
      const rowNumberByVariant = {
        year: 1,
        quarter: 3,
        cohortYear: 4,
      };

      return times(rowNumberByVariant[variant], n => ({
        key: `loading-row-${n}`,
        ...(times(12).reduce((memo, date) => ({
          ...memo,
          [date]: <Skeleton height={10} width="80%" />,
          [`${date}_td_props`]: {
            className: ' d-table-cell d-td td-date text-center',
            style: {
              width: '6.69%',
            },
          },
        }), {})),
      }));
    }

    if (variant === 'year') {
      return [tableData.reduce((memo, [timestamp, val]) => ({
        ...memo,
        [timestamp]: `${numberToHumanChart(val)}%`,
        [`${timestamp}_td_props`]: {
          className: numberToHumanChart(val) < 0 ? 'negative-column' : '',
        },
      }), {})];
    }

    if (variant === 'cohortYear') {
      return reduce(tableData.rows, (memo, rowData, rowKey) => memo.concat({
        name: tableData.leadColumnLabels[rowKey],
        ...reduce(rowData, (result, [timestamp, val]) => {
          const getCellValue = () => {
            if (isNil(val)) return rowKey.includes('change') ? '—' : 'N/A';

            const valueUnit = tableData.valueUnits[rowKey] || '';
            return `${numberToHumanChart(val)}${valueUnit}`;
          };

          const getCellProps = () => {
            if (isNil(val)) return { className: 'empty-column' };
            return { className: (rowKey.includes('change') && numberToHumanChart(val) < 0) ? 'negative-column' : '' };
          };

          return ({
            ...result,
            [timestamp]: getCellValue(),
            [`${timestamp}_td_props`]: getCellProps(),
          });
        }, {}),
      }), []);
    }

    if (variant === 'quarter') {
      const rowKeys = ['curr_period', 'prev_period'];
      const leadRowKey = 'prev_period';

      const changeRow = {
        name: '% Change',
        ...reduce(tableData[leadRowKey], (result, { date, value }, index) => {
          const currPeriodItem = tableData.curr_period[index];

          const getCellValue = () => {
            if (isNil(value) || isNil(currPeriodItem?.value)) return '—';
            if (value === 0) return '100%';

            return `${numberToHumanChart(((currPeriodItem.value - value) * 100) / value)}%`;
          };

          const getCellProps = () => {
            if (isNil(value) || isNil(currPeriodItem?.value)) return { className: 'empty-column' };
            return { className: parseFloat(getCellValue()) < 0 ? 'negative-column' : '' };
          };

          return ({
            ...result,
            [date]: getCellValue(),
            [`${date}_td_props`]: getCellProps(),
          });
        }, {}),
      };

      return reduce(rowKeys, (memo, rowKey) => memo.concat({
        name: getFullYear(last(tableData[rowKey])?.date),
        ...reduce(tableData[leadRowKey], (result, { value, date }, index) => {
          const cellKey = rowKey === leadRowKey ? date : tableData[leadRowKey][index]?.date;
          const cellValue = rowKey === leadRowKey ? value : tableData[rowKey][index]?.value;

          const getCellValue = () => {
            if (isNil(cellValue)) return 'N/A';

            const valueUnit = tableData.valueUnits?.[rowKey] || '';
            return `${numberToHumanChart(cellValue)}${valueUnit}`;
          };

          const getCellProps = () => {
            if (isNil(cellValue)) return { className: 'empty-column' };
            return {};
          };

          return ({
            ...result,
            [cellKey]: getCellValue(),
            [`${cellKey}_td_props`]: getCellProps(),
          });
        }, {}),
      }), []).concat(changeRow);
    }

    return [];
  }, [loading, tableData, variant]);

  return (
    <div
      ref={tableLayoutRef}
      className={cx('app-finance-table-container', {
        'r-scroll-active': scrollState.right,
        'l-scroll-active': scrollState.left,
      })}
    >
      <div ref={tableWrapperRef} className={cx('app-finance-table-wrapper', { 'slide-in-out-top': show })}>
        <div className="animation-fade-element" />
        <SortableTable
          tableClassName={cx('table app-finance-table', tableClassNameByVariant[variant])}
          rows={rows}
          columns={columns}
          emptyStateText="No data to display"
          showEmptyCells={false}
          showMoreCellColSpan={columns.length}
          showMoreCellSimplified
        />
      </div>
    </div>
  );
};

ChartTable.propTypes = {
  show: T.bool,
  loading: T.bool,
  variant: T.oneOf(['year', 'cohortYear', 'quarter']),
  tableData: T.oneOfType([T.object, T.array]),
  tableWrapperRef: T.object,
  tableLayoutRef: T.object,
  scrollState: T.object,
};

export default ChartTable;
